/**
 * AppHeader — top bar: brand, view nav, stub pill, theme toggle and the
 * auth UserChip. Extracted from App.js during the code-quality refactor.
 */
import { useEffect, useState } from "react";
import axios from "axios";

import { BACKEND_URL } from "../lib/beaconUtils";

const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "context", label: "Context Engine" },
  { key: "quick", label: "Quick Comms" },
  { key: "plan", label: "Annual Plan" },
  { key: "programs", label: "Programs" },
  { key: "library", label: "Library" },
];

export function AppHeader({
  activeView, setActiveView, theme, setTheme, health,
  authUser, onSignOut, onSignIn,
}) {
  const navItems = authUser?.role === "admin"
    ? [...NAV_ITEMS, { key: "admin", label: "Admin" }]
    : NAV_ITEMS;
  useEffect(() => {
    // Publish the live header height so sticky page-top sections
    // (.beacon-sticky-head) pin exactly beneath it.
    const el = document.querySelector(".beacon-header");
    if (!el) return undefined;
    const set = () => document.documentElement.style.setProperty(
      "--beacon-header-h", `${el.offsetHeight}px`,
    );
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <header className="beacon-header">
      <div className="beacon-header-inner">
        <div className="beacon-brand">
          <span className="beacon-wordmark" data-testid="beacon-wordmark">
            BEACON
          </span>
          <span className="beacon-divider">//</span>
          <span className="beacon-tagline">H&amp;S COMMS, CALIBRATED.</span>
        </div>
        <div className="beacon-meta">
          <nav className="beacon-nav" data-testid="view-nav">
            {navItems.map((item) => (
              <button
                key={item.key}
                type="button"
                data-testid={`nav-${item.key}`}
                className={`beacon-nav-btn${activeView === item.key ? " is-active" : ""}`}
                onClick={() => setActiveView(item.key)}
              >
                {item.label}
              </button>
            ))}
          </nav>
          {health?.stub_mode && (
            <span className="beacon-stub-pill" data-testid="stub-mode-pill">
              STUB MODE
            </span>
          )}
          <button
            type="button"
            className="beacon-theme-toggle"
            data-testid="theme-toggle"
            aria-label={
              theme === "dark"
                ? "Switch to light theme"
                : "Switch to dark theme"
            }
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            onClick={() =>
              setTheme((prev) => (prev === "dark" ? "light" : "dark"))
            }
          >
            <span className="beacon-theme-toggle-track">
              <span
                className={`beacon-theme-toggle-thumb is-${theme}`}
                data-testid={`theme-toggle-thumb-${theme}`}
              />
              <span className="beacon-theme-toggle-icon beacon-theme-toggle-icon-sun">
                ☀
              </span>
              <span className="beacon-theme-toggle-icon beacon-theme-toggle-icon-moon">
                ☾
              </span>
            </span>
          </button>
          <span className="beacon-version">v0.4 · BLOCK 4</span>
          <UserChip user={authUser} onSignOut={onSignOut} onSignIn={onSignIn}
            onOpenHelp={() => setActiveView("help")} />
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// UserChip — auth status pill in the app header
// ---------------------------------------------------------------------------

function UserChip({ user, onSignOut, onSignIn, onOpenHelp }) {
  const [open, setOpen] = useState(false);
  const [nudges, setNudges] = useState(user?.email_nudges !== false);
  useEffect(() => {
    setNudges(user?.email_nudges !== false);
  }, [user]);
  const toggleNudges = async () => {
    const next = !nudges;
    setNudges(next);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/auth/me/preferences`,
        { email_nudges: next },
        { withCredentials: true },
      );
    } catch {
      setNudges(!next);
    }
  };
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      // Ignore clicks that originated inside the chip's popover.
      if (e.target.closest?.('[data-testid="user-chip"]')) return;
      setOpen(false);
    };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  if (!user) {
    return (
      <button
        type="button"
        className="beacon-user-chip beacon-user-chip-guest"
        data-testid="user-chip-guest"
        onClick={onSignIn}
        title="Sign in with Google to persist your context"
      >
        <span className="beacon-user-chip-guest-dot" aria-hidden="true" />
        Guest · Sign in
      </button>
    );
  }
  const initials = (user.name || user.email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="beacon-user-chip-wrap" data-testid="user-chip">
      <button
        type="button"
        className="beacon-user-chip"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {user.picture ? (
          // Note: some Google avatars 403 in cross-origin img requests;
          // the initials fallback covers that case.
          <img
            className="beacon-user-chip-avatar"
            src={user.picture}
            alt=""
            referrerPolicy="no-referrer"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : null}
        <span className="beacon-user-chip-initials" aria-hidden="true">
          {initials}
        </span>
        <span className="beacon-user-chip-name">
          {(user.name || user.email || "").split(" ")[0]}
        </span>
        <span className="beacon-user-chip-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="beacon-user-chip-menu" role="menu">
          <div className="beacon-user-chip-menu-head">
            <div className="beacon-user-chip-menu-name">{user.name}</div>
            <div className="beacon-user-chip-menu-email">{user.email}</div>
          </div>
          <button
            type="button"
            className="beacon-user-chip-menu-item beacon-user-chip-menu-toggle"
            data-testid="email-nudge-toggle"
            role="menuitemcheckbox"
            aria-checked={nudges}
            onClick={toggleNudges}
          >
            <span>Monday email nudge</span>
            <span
              className={`beacon-mini-switch${nudges ? " is-on" : ""}`}
              aria-hidden="true"
            >
              <span className="beacon-mini-switch-thumb" />
            </span>
          </button>
          <button
            type="button"
            className="beacon-user-chip-menu-item"
            data-testid="menu-help"
            onClick={() => { setOpen(false); onOpenHelp?.(); }}
          >
            Help &amp; FAQ
          </button>
          <button
            type="button"
            className="beacon-user-chip-menu-item"
            data-testid="sign-out"
            onClick={() => { setOpen(false); onSignOut(); }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
